import { useCallback, useEffect, useRef, useState } from "react";
import { getRetryDelaySeconds } from "@ease/shared";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/lib/auth-provider";

interface RealtimeEnvelope {
  type?: string;
  payload?: Record<string, unknown>;
}

const INBOX_CHANNELS = [
  "notification.inbound_message",
  "notification.*",
];

export const __inboxRealtimeInternals = {
  buildRealtimeWebsocketUrl,
  parseEnvelope,
};

export function useInboxRealtime(onInboxEvent: (envelope: RealtimeEnvelope) => void) {
  const { isLoading } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const onInboxEventRef = useRef(onInboxEvent);

  onInboxEventRef.current = onInboxEvent;

  const connect = useCallback(async () => {
    if (isLoading) {
      return;
    }

    const token = await requestBearerToken();
    if (!token) {
      return;
    }

    wsRef.current?.close();
    const ws = new WebSocket(buildRealtimeWebsocketUrl(resolveRealtimeBaseUrl(), token));
    wsRef.current = ws;
    ws.addEventListener("open", () => {
      setIsConnected(true);
      reconnectAttempts.current = 0;
    });
    ws.addEventListener("message", (event) => {
      const parsed = parseEnvelope(event.data);
      if (parsed) {
        onInboxEventRef.current(parsed);
      }
    });
    ws.addEventListener("close", () => {
      setIsConnected(false);
      if (reconnectAttempts.current >= 10) {
        return;
      }

      reconnectAttempts.current += 1;
      const delayMs = getRetryDelaySeconds(reconnectAttempts.current, { baseMs: 1000 }) * 1000;
      reconnectTimer.current = setTimeout(() => {
        void connect();
      }, delayMs);
    });
  }, [isLoading]);

  useEffect(() => {
    void connect();
    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);

  return { isConnected };
}

async function requestBearerToken(): Promise<string | null> {
  try {
    const result = await authClient.getSession();
    const candidates = [
      pickValue(result, ["data", "session", "token"]),
      pickValue(result, ["data", "token"]),
      pickValue(result, ["token"]),
    ];
    return candidates.find((value): value is string => typeof value === "string" && value.length > 0) ?? null;
  } catch {
    return null;
  }
}

function resolveRealtimeBaseUrl(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env.VITE_REALTIME_URL?.trim() || window.location.origin;
}

function buildRealtimeWebsocketUrl(baseUrl: string, token: string): string {
  const url = new URL(baseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws";
  url.searchParams.set("token", token);
  url.searchParams.set("channels", INBOX_CHANNELS.join(","));
  return url.toString();
}

function pickValue(input: unknown, path: string[]): unknown {
  let current: unknown = input;
  for (const segment of path) {
    if (!current || typeof current !== "object" || !(segment in current)) {
      return null;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function parseEnvelope(raw: unknown): RealtimeEnvelope | null {
  try {
    return JSON.parse(typeof raw === "string" ? raw : String(raw)) as RealtimeEnvelope;
  } catch {
    return null;
  }
}
